import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Calendar from 'react-calendar';
import type { CalendarTileProperties } from 'react-calendar';
import {
  CalendarDaySchedule,
  CalendarPlan,
  CalendarPlanData,
  CalendarScheduleItem,
  CalendarSummaryItem,
} from '../types';
import {
  createCalendarEvent,
  deleteCalendarEvent,
  deleteCalendarPlan,
  fetchCalendarDetail,
  fetchCalendarList,
  generateCalendarPlan,
} from '../services/calendarService';
import './HealthCalendarPage.css';

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseAsLocalDate = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const localDate = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(localDate.getTime()) ? null : localDate;
};

const safeParseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const localParsed = parseAsLocalDate(value);
  if (localParsed) return localParsed;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatZhFullDate = (date: Date) =>
  `${date.getFullYear()}年${`${date.getMonth() + 1}`.padStart(2, '0')}月${`${date.getDate()}`.padStart(2, '0')}日`;

const formatZhMonthDay = (date: Date) => `${date.getMonth() + 1}月${date.getDate()}日`;

const formatWeekday = (date: Date) => {
  const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
  return `星期${weekdays[date.getDay()]}`;
};

const getTodayISO = () => formatDateKey(new Date());

const DAY_RANGE_OPTIONS: Array<{ value: number; label: string; description: string }> = [
  { value: 3, label: '3 天', description: '短期强化调理' },
  { value: 5, label: '5 天', description: '一周前后的节奏' },
  { value: 7, label: '7 天', description: '完整一周规划' },
  { value: 10, label: '10 天', description: '轻量养生旅程' },
  { value: 14, label: '14 天', description: '半月巩固调护' },
  { value: 21, label: '21 天', description: '阶段性干预' },
  { value: 30, label: '30 天', description: '整月节律养护' },
];

const getSeasonEmoji = (season?: string | null) => {
  if (!season) return '📅';
  if (season.includes('春')) return '🌸';
  if (season.includes('夏')) return '☀️';
  if (season.includes('秋')) return '🍂';
  if (season.includes('冬')) return '❄️';
  return '📅';
};

const normalizeSchedule = (dailySchedule?: CalendarDaySchedule[] | null) => {
  if (!Array.isArray(dailySchedule)) {
    return [] as CalendarDaySchedule[];
  }
  return dailySchedule
    .filter((entry) => entry && typeof entry === 'object' && typeof entry.date === 'string')
    .map((entry) => ({
      ...entry,
      items: Array.isArray(entry.items) ? entry.items.filter((item) => item && typeof item === 'object') : [],
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

const HealthCalendarPage: React.FC = () => {
  const [startDateInput, setStartDateInput] = useState<string>(getTodayISO());
  const [rangeDays, setRangeDays] = useState<number>(7);
  const [constitution, setConstitution] = useState<string | null>(null);
  const [calendarPlan, setCalendarPlan] = useState<CalendarPlan | null>(null);
  const [calendarList, setCalendarList] = useState<CalendarSummaryItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [calendarViewDate, setCalendarViewDate] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [historyDeleting, setHistoryDeleting] = useState<string | null>(null);
  const [activeCalendarId, setActiveCalendarId] = useState<string | null>(null);
  const initialHistoryAppliedRef = useRef(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    time: '',
    description: '',
    category: '',
  });

  const applyPlanDetail = useCallback((detail: CalendarPlan) => {
    const normalized = normalizeSchedule(detail.plan_data?.daily_schedule);
    const earliest = normalized.length > 0 ? safeParseDate(normalized[0].date) : null;
    const start = safeParseDate(detail.plan_data?.start_date ?? null) ?? earliest;
    setCalendarPlan(detail);
    initialHistoryAppliedRef.current = true;
    setActiveCalendarId(detail.calendar_id);
    if (start) {
      setCalendarViewDate(start);
      setSelectedDate(start);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      try {
        const data = await fetchCalendarList();
        if (!mounted) return;
        setCalendarList(data ?? []);

        const firstPlan = data && data.length > 0 ? data[0] : null;
        if (!firstPlan) return;

        if (initialHistoryAppliedRef.current) return;
        initialHistoryAppliedRef.current = true;

        try {
          const detail = await fetchCalendarDetail(firstPlan.calendar_id);
          if (!mounted) return;
          applyPlanDetail(detail);
        } catch (err) {
          console.error('自动加载最近日程失败:', err);
        }
      } catch (err) {
        console.error('加载日历列表失败:', err);
      }
    };

    void boot();

    return () => {
      mounted = false;
    };
  }, [applyPlanDetail]);

  const planData: CalendarPlanData | null = calendarPlan?.plan_data ?? null;
  const planStartDate = useMemo(() => safeParseDate(planData?.start_date), [planData?.start_date]);
  const planEndDate = useMemo(() => safeParseDate(planData?.end_date), [planData?.end_date]);
  const previousPlanIdRef = useRef<string | null>(null);

  const normalizedSchedule = useMemo(
    () => normalizeSchedule(planData?.daily_schedule),
    [planData?.daily_schedule],
  );

  const scheduleMap = useMemo(() => {
    const map = new Map<string, CalendarDaySchedule>();
    normalizedSchedule.forEach((entry) => {
      map.set(entry.date, entry);
    });
    return map;
  }, [normalizedSchedule]);

  const schedulePreview = useMemo(() => normalizedSchedule.slice(0, 3), [normalizedSchedule]);

  useEffect(() => {
    const planId = calendarPlan?.calendar_id ?? null;
    if (!planId) {
      previousPlanIdRef.current = null;
      setActiveCalendarId(null);
      return;
    }

    const isNewPlan = previousPlanIdRef.current !== planId;
    if (isNewPlan) {
      previousPlanIdRef.current = planId;
    }

    const fallbackStart = calendarPlan
      ? new Date(calendarPlan.year, (calendarPlan.month ?? 1) - 1, 1)
      : null;
  const scheduleEntries = normalizedSchedule;
    const scheduleStart = scheduleEntries.length > 0 ? safeParseDate(scheduleEntries[0].date) : null;
    const initialDate = planStartDate ?? scheduleStart ?? fallbackStart;

    if (!initialDate) {
      return;
    }

    if (isNewPlan) {
      setCalendarViewDate(initialDate);
      setSelectedDate(initialDate);
      return;
    }

    if (!selectedDate) {
      setCalendarViewDate(initialDate);
      setSelectedDate(initialDate);
    }
  }, [calendarPlan, planStartDate, planEndDate, selectedDate, normalizedSchedule]);

  const selectedSchedule = useMemo(() => {
    if (!selectedDate) return undefined;
    return scheduleMap.get(formatDateKey(selectedDate));
  }, [scheduleMap, selectedDate]);

  const selectedEvents: CalendarScheduleItem[] = selectedSchedule?.items ?? [];

  const handleGenerate = useCallback(async () => {
    if (!startDateInput) {
      setError('请选择开始日期');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const payload = {
        start_date: startDateInput,
        days: rangeDays,
        constitution: constitution || undefined,
      };
      const plan = await generateCalendarPlan(payload);
      applyPlanDetail(plan);
      setCalendarList(await fetchCalendarList());
    } catch (err) {
      console.error('生成养生日程失败', err);
      setError(err instanceof Error ? err.message : '生成失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [applyPlanDetail, constitution, rangeDays, startDateInput]);

  const loadCalendar = useCallback(async (calendarId: string) => {
    try {
      const detail = await fetchCalendarDetail(calendarId);
      applyPlanDetail(detail);
    } catch (err) {
      console.error('加载日历详情失败:', err);
    }
  }, [applyPlanDetail]);

  const handleAddEvent = useCallback(async () => {
    if (!calendarPlan || !selectedDate) {
      setError('请先选择日期');
      return;
    }
    const title = eventForm.title.trim();
    if (!title) {
      setError('请填写日程标题');
      return;
    }

    setError(null);
    setEventSubmitting(true);
    try {
      const updated = await createCalendarEvent(calendarPlan.calendar_id, {
        date: formatDateKey(selectedDate),
        title,
        description: eventForm.description.trim() || undefined,
        time_of_day: eventForm.time.trim() || undefined,
        category: eventForm.category.trim() || undefined,
      });
      setCalendarPlan(updated);
      setCalendarList(await fetchCalendarList());
      setEventForm({ title: '', time: '', description: '', category: '' });
    } catch (err) {
      console.error('添加日程失败:', err);
      setError(err instanceof Error ? err.message : '新增日程失败，请稍后重试');
    } finally {
      setEventSubmitting(false);
    }
  }, [calendarPlan, eventForm, selectedDate]);

  const handleDeleteEvent = useCallback(
    async (eventId: string) => {
      if (!calendarPlan) return;
      setEventSubmitting(true);
      try {
        const updated = await deleteCalendarEvent(calendarPlan.calendar_id, eventId);
        setCalendarPlan(updated);
        setCalendarList(await fetchCalendarList());
      } catch (err) {
        console.error('删除日程失败:', err);
        setError(err instanceof Error ? err.message : '删除日程失败，请稍后重试');
      } finally {
        setEventSubmitting(false);
      }
    },
    [calendarPlan],
  );

  const handleDeleteHistory = useCallback(
    async (calendarId: string) => {
      if (!calendarId) return;
      const confirmed = window.confirm('确认删除这条历史日程吗？删除后将无法恢复。');
      if (!confirmed) return;

      setError(null);
      setHistoryDeleting(calendarId);
      try {
        await deleteCalendarPlan(calendarId);
        const refreshedList = await fetchCalendarList();
        const nextList = refreshedList ?? [];
        setCalendarList(nextList);
        if (calendarPlan?.calendar_id === calendarId) {
          setCalendarPlan(null);
          setSelectedDate(null);
          setCalendarViewDate(new Date());
          setActiveCalendarId(null);

          const fallbackPlan = nextList.find((item) => item.calendar_id !== calendarId) || nextList[0];
          if (fallbackPlan) {
            try {
              const detail = await fetchCalendarDetail(fallbackPlan.calendar_id);
              applyPlanDetail(detail);
            } catch (fallbackError) {
              console.error('加载备用日程失败:', fallbackError);
            }
          }
        }
      } catch (err) {
        console.error('删除历史日程失败:', err);
        setError(err instanceof Error ? err.message : '删除历史日程失败，请稍后重试');
      } finally {
        setHistoryDeleting(null);
      }
    },
    [applyPlanDetail, calendarPlan],
  );

  const tileClassName = useCallback(
    ({ date, view }: CalendarTileProperties) => {
      if (view !== 'month') return undefined;
      const key = formatDateKey(date);
      const events = scheduleMap.get(key)?.items ?? [];
      const classes: string[] = ['calendar-day'];
      if (planStartDate && planEndDate) {
        if (date >= planStartDate && date <= planEndDate) {
          classes.push('in-range');
        } else {
          classes.push('out-of-range');
        }
      }
      if (events.length > 0) {
        classes.push('has-events');
      }
      return classes.join(' ');
    },
    [planEndDate, planStartDate, scheduleMap],
  );

  const tileContent = useCallback(
    ({ date, view }: CalendarTileProperties) => {
      if (view !== 'month') return null;
      const events = scheduleMap.get(formatDateKey(date))?.items ?? [];
      if (events.length === 0) return null;
      return (
        <div className="calendar-day-indicator">
          <span>{events.length}</span>
        </div>
      );
    },
    [scheduleMap],
  );

  const currentSeason = planData?.season ?? calendarPlan?.season;
  const dayCount = planData?.day_count;
  const totalEvents = selectedSchedule?.items?.length ?? 0;
  const timelineTitle = selectedDate ? formatZhMonthDay(selectedDate) : '请选择日期';
  const selectedScheduleSummary = selectedSchedule?.summary || '这一天暂未设定摘要，欢迎自行添加日程。';

  return (
    <div className="calendar-page">
      <motion.div
        className="calendar-shell"
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="calendar-grid">
          <aside className="calendar-side">
            <div className="calendar-day-card">
              <div className="day-label">{selectedDate ? formatWeekday(selectedDate) : '等待选择'}</div>
              <div className="day-number">{selectedDate ? selectedDate.getDate() : '--'}</div>
              <div className="day-date">{selectedDate ? formatZhFullDate(selectedDate) : '选择右侧的日期以查看详情'}</div>
              <div className="day-focus">{selectedSchedule?.focus || '今日暂无重点提醒'}</div>
              <div className="day-meta">
                <span>日程 {totalEvents} 项</span>
                {currentSeason && <span>{getSeasonEmoji(currentSeason)} {currentSeason}</span>}
              </div>
              <p className="day-summary">{selectedScheduleSummary}</p>
            </div>

            <div className="calendar-card">
              <div className="calendar-card-header">
                <h3>生成 AI 日程</h3>
                <p>选择开始日期与天数，系统将为每一天自动填充养生安排。</p>
              </div>
              <label className="field-label" htmlFor="calendar-start-date">开始日期</label>
              <input
                id="calendar-start-date"
                type="date"
                value={startDateInput}
                onChange={(event) => setStartDateInput(event.target.value)}
              />

              <div className="range-selector">
                {DAY_RANGE_OPTIONS.map((option) => {
                  const active = rangeDays === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={active ? 'active' : ''}
                      onClick={() => setRangeDays(option.value)}
                    >
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </button>
                  );
                })}
              </div>

              <label className="field-label" htmlFor="calendar-constitution">体质类型</label>
              <select
                id="calendar-constitution"
                value={constitution ?? ''}
                onChange={(event) => setConstitution(event.target.value || null)}
              >
                <option value="">根据最新体质识别自动匹配</option>
                {['平和质', '气虚质', '阳虚质', '阴虚质', '痰湿质', '湿热质', '血瘀质', '气郁质', '特禀质'].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>

              {error && <div className="calendar-error">{error}</div>}

              <motion.button
                type="button"
                className="calendar-primary-btn"
                onClick={handleGenerate}
                disabled={loading}
                whileHover={{ translateY: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                {loading ? '正在生成...' : '生成每日养生日程'}
              </motion.button>
            </div>

            <div className="calendar-card history-card">
              <div className="calendar-card-header">
                <h3>历史日程</h3>
                <p>回顾过往 AI 生成或自定义的养生节奏。</p>
              </div>
              {calendarList.length === 0 ? (
                <div className="calendar-empty">暂未生成过养生日程</div>
              ) : (
                <ul className="calendar-history-list">
                  {calendarList.map((item) => (
                    <li key={item.calendar_id}>
                      <div className="history-entry">
                        <button
                          type="button"
                          className={`history-main${activeCalendarId === item.calendar_id ? ' active' : ''}`}
                          onClick={() => loadCalendar(item.calendar_id)}
                        >
                          <div className="history-badge">{getSeasonEmoji(item.plan_summary?.season)}</div>
                          <div className="history-text">
                            <strong>{item.plan_summary?.start_date && item.plan_summary?.end_date
                              ? `${item.plan_summary.start_date} ~ ${item.plan_summary.end_date}`
                              : `${item.year} 年${item.month ? ` ${item.month} 月` : ''}`}</strong>
                            <span>{item.constitution || '综合体质'} · {item.plan_summary?.total_events ?? 0} 条日程</span>
                          </div>
                          <span className="history-arrow">→</span>
                        </button>
                        <button
                          type="button"
                          className="history-delete"
                          onClick={() => handleDeleteHistory(item.calendar_id)}
                          disabled={historyDeleting === item.calendar_id}
                        >
                          {historyDeleting === item.calendar_id ? '删除中...' : '删除'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {calendarPlan && (
              <div className="calendar-outline">
                <div className="calendar-card-header">
                  <h3>调养纲要</h3>
                  <p>AI 生成的纲要已整理为每日重点，点击下方按钮可查看完整详情。</p>
                </div>
                {schedulePreview.length > 0 ? (
                  <ul className="outline-list">
                    {schedulePreview.map((entry) => {
                      const entryDate = safeParseDate(entry.date);
                      return (
                        <li key={entry.date}>
                          <div className="outline-date">{entryDate ? formatZhMonthDay(entryDate) : entry.date}</div>
                          <div className="outline-body">
                            <strong>{entry.focus || '重点待整理'}</strong>
                            <p>{entry.summary || '模型未提供摘要，可在详情页查看完整内容。'}</p>
                            {entry.tags && entry.tags.length > 0 && (
                              <div className="outline-tags">
                                {entry.tags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="calendar-empty">暂未生成纲要内容，可先创建日程。</div>
                )}

                {calendarPlan?.calendar_id && (
                  <Link className="outline-link" to={`/app/health-calendar/detail/${calendarPlan.calendar_id}`}>
                    查看完整纲要
                  </Link>
                )}
              </div>
            )}
          </aside>

          <section className="calendar-main">
            <header className="calendar-hero">
              <div>
                <span className="calendar-hero-badge">个性化节律</span>
                <h1>AI 养生日程助手</h1>
                <p>
                  将您的体质信息与节气节律融合，输出逐日可执行的养生安排，支持手动增删与实时同步。
                </p>
              </div>
              <div className="calendar-hero-meta">
                <div>
                  <span>范围</span>
                  <strong>{planStartDate && planEndDate
                    ? `${formatZhMonthDay(planStartDate)} ~ ${formatZhMonthDay(planEndDate)}`
                    : '未生成'}</strong>
                </div>
                <div>
                  <span>天数</span>
                  <strong>{dayCount ?? '--'} 天</strong>
                </div>
                <div>
                  <span>体质</span>
                  <strong>{calendarPlan?.constitution || constitution || '自动匹配'}</strong>
                </div>
                <div>
                  <span>季节</span>
                  <strong>{getSeasonEmoji(currentSeason)} {currentSeason || '待生成'}</strong>
                </div>
              </div>
            </header>

            <div className="calendar-board">
              <Calendar
                value={selectedDate ?? calendarViewDate}
                onClickDay={(date: Date) => {
                  setSelectedDate(date);
                }}
                activeStartDate={calendarViewDate}
                onActiveStartDateChange={({ activeStartDate }: { activeStartDate: Date | null }) => {
                  if (activeStartDate) {
                    setCalendarViewDate(activeStartDate);
                  }
                }}
                tileClassName={tileClassName}
                tileContent={tileContent}
                minDetail="month"
              />
            </div>

            <div className="calendar-schedule-panel">
              <div className="schedule-header">
                <div>
                  <h3>{timelineTitle}</h3>
                  <p>{selectedScheduleSummary}</p>
                </div>
                <div className="schedule-count">共 {selectedEvents.length} 项安排</div>
              </div>

              {selectedEvents.length === 0 ? (
                <div className="calendar-empty">该日期暂无日程，您可以在下方新增自定义安排。</div>
              ) : (
                <ul className="schedule-list">
                  {selectedEvents.map((item, index) => {
                    const fallbackKey = `${selectedDate ? formatDateKey(selectedDate) : 'unknown'}-${index}`;
                    const itemKey = item.event_id || fallbackKey;
                    return (
                      <li key={itemKey}>
                      <div className="schedule-time">{item.time_of_day || '整日'}</div>
                      <div className="schedule-body">
                        <strong>{item.title}</strong>
                        {item.description && <p>{item.description}</p>}
                        <div className="schedule-tags">
                          {item.category && <span className="tag">{item.category}</span>}
                          <span className={`tag ${item.source === 'manual' ? 'manual' : 'ai'}`}>
                            {item.source === 'manual' ? '手动添加' : 'AI 自动生成'}
                          </span>
                        </div>
                      </div>
                      {item.source === 'manual' && (
                        <button
                          type="button"
                          className="schedule-delete"
                          onClick={() => handleDeleteEvent(item.event_id)}
                          disabled={eventSubmitting}
                        >
                          删除
                        </button>
                      )}
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="schedule-form">
                <h4>新增日程</h4>
                <div className="form-grid">
                  <div>
                    <label htmlFor="event-time">时间</label>
                    <input
                      id="event-time"
                      type="time"
                      value={eventForm.time}
                      onChange={(event) => setEventForm((prev) => ({ ...prev, time: event.target.value }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="event-title">标题</label>
                    <input
                      id="event-title"
                      type="text"
                      value={eventForm.title}
                      onChange={(event) => setEventForm((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder="例如：晨起温补姜枣水"
                    />
                  </div>
                  <div>
                    <label htmlFor="event-category">类别</label>
                    <input
                      id="event-category"
                      type="text"
                      value={eventForm.category}
                      onChange={(event) => setEventForm((prev) => ({ ...prev, category: event.target.value }))}
                      placeholder="饮食 / 运动 / 情志"
                    />
                  </div>
                  <div className="form-full">
                    <label htmlFor="event-desc">描述</label>
                    <textarea
                      id="event-desc"
                      value={eventForm.description}
                      onChange={(event) => setEventForm((prev) => ({ ...prev, description: event.target.value }))}
                      placeholder="补充细节或注意事项"
                      rows={2}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="calendar-secondary-btn"
                  onClick={handleAddEvent}
                  disabled={eventSubmitting}
                >
                  {eventSubmitting ? '处理中...' : '添加到日程'}
                </button>
              </div>
            </div>
          </section>
        </div>
      </motion.div>
    </div>
  );
};

export default HealthCalendarPage;
